import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { App } from '@capacitor/app';

type AppState = {
  isAppActive: boolean;
  isAppActiveRef: MutableRefObject<boolean>;
  isOnline: boolean;
  isOnlineRef: MutableRefObject<boolean>;
};

/**
 * Tracks app foreground/background state and network connectivity.
 *
 * Exposes both React state (for rendering) and refs (for reading inside
 * stable closures like socket event callbacks registered once).
 */
const useAppState = (): AppState => {
  const [isAppActive, setIsAppActive] = useState(!document.hidden);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const isAppActiveRef = useRef(!document.hidden);
  const isOnlineRef = useRef(navigator.onLine);

  // Keep refs in sync with state
  useEffect(() => {
    isAppActiveRef.current = isAppActive;
  }, [isAppActive]);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  // --- App visibility ---
  useEffect(() => {
    const onVisibilityChange = () => {
      const active = !document.hidden;
      setIsAppActive(active);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Capacitor native: more reliable than visibilitychange on iOS/Android
    let nativeListener: { remove: () => void } | undefined;

    if (window.env?.isApp) {
      App.addListener('appStateChange', ({ isActive }) => {
        setIsAppActive(isActive);
      }).then((listener) => {
        nativeListener = listener;
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      nativeListener?.remove();
    };
  }, []);

  // --- Network connectivity ---
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return { isAppActive, isAppActiveRef, isOnline, isOnlineRef };
};

export default useAppState;
